/**
 * Artboard positions on the moodboard workspace (Illustrator-style board).
 * Coords are in "board units": one artboard is typically w=1 × h=A4 landscape ratio.
 * Stored on each page as `page.board = { x, y, w, h }`. PDF ignores this.
 */

import { PAGE_A4_LANDSCAPE_RATIO } from "./skillAgentMoodboardViewZoom";

export const DEFAULT_ARTBOARD_W = 1;
export const DEFAULT_ARTBOARD_H = PAGE_A4_LANDSCAPE_RATIO;
export const ARTBOARD_GAP = 0.15;

/** Pixels per board unit in the workspace world (CSS). */
export const BOARD_PX_PER_UNIT = 900;

/** Rango estilo Illustrator (~3.13% … 6400%). */
export const BOARD_CAM_ZOOM_MIN = 0.03125;
export const BOARD_CAM_ZOOM_MAX = 64;
export const BOARD_CAM_ZOOM_STEP = 0.1;

export const WORKSPACE_MODE_PAGE = "page";
export const WORKSPACE_MODE_BOARD = "board";
export const WORKSPACE_MODE_STORAGE_KEY = "crm.moodboard.workspaceMode";

/** Illustrator Medium Grey pasteboard (user sample). */
export const DEFAULT_PASTEBOARD_BG = "#8c8e8d";
export const PASTEBOARD_BG_STORAGE_KEY = "crm.moodboard.pasteboardBg";

const HEX6 = /^#[0-9a-fA-F]{6}$/;

export function normalizePasteboardBg(raw) {
  const s = String(raw || "").trim();
  if (HEX6.test(s)) return s.toLowerCase();
  return DEFAULT_PASTEBOARD_BG;
}

export function readStoredPasteboardBg() {
  if (typeof window === "undefined") return DEFAULT_PASTEBOARD_BG;
  try {
    return normalizePasteboardBg(
      window.localStorage.getItem(PASTEBOARD_BG_STORAGE_KEY),
    );
  } catch {
    return DEFAULT_PASTEBOARD_BG;
  }
}

export function writeStoredPasteboardBg(hex) {
  if (typeof window === "undefined") return;
  const v = normalizePasteboardBg(hex);
  try {
    window.localStorage.setItem(PASTEBOARD_BG_STORAGE_KEY, v);
  } catch {
    /* ignore */
  }
}

/** Default floating photo size on the pasteboard (board units). */
export const PASTEBOARD_ITEM_W = 0.22;
export const PASTEBOARD_ITEM_H = 0.22;
/** Tope solo para floats sin origen de mesa (subida directa al fondo). */
export const PASTEBOARD_ITEM_MAX = 0.92;

let pasteboardIdSeq = 0;
export function newPasteboardId(prefix = "pb") {
  pasteboardIdSeq += 1;
  return `${prefix}_${Date.now().toString(36)}_${pasteboardIdSeq}`;
}

/** Escala w/h juntos para respetar aspect. */
function clampPasteboardSize(rawW, rawH, maxSide = PASTEBOARD_ITEM_MAX) {
  let w = Math.max(0.02, finiteOr(rawW, PASTEBOARD_ITEM_W));
  let h = Math.max(0.02, finiteOr(rawH, PASTEBOARD_ITEM_H));
  const maxDim = Math.max(w, h);
  const cap = Number.isFinite(maxSide) ? maxSide : PASTEBOARD_ITEM_MAX;
  if (maxDim > cap) {
    const s = cap / maxDim;
    w *= s;
    h *= s;
  }
  return { w, h };
}

export function normalizePasteboardItem(raw) {
  if (!raw || typeof raw !== "object") return null;
  // Marca (texto/figura/chrome) flotando en el fondo del tablero
  if (String(raw.kind || "").toLowerCase() === "mark" && raw.mark) {
    const w = Math.max(0.02, finiteOr(raw.w, PASTEBOARD_ITEM_W));
    const h = Math.max(0.02, finiteOr(raw.h, PASTEBOARD_ITEM_H));
    return {
      id: String(raw.id || newPasteboardId("pbm")),
      kind: "mark",
      mark: raw.mark,
      x: finiteOr(raw.x, 0),
      y: finiteOr(raw.y, DEFAULT_ARTBOARD_H + ARTBOARD_GAP),
      w,
      h,
    };
  }
  const assetId = Number(raw.assetId);
  if (!Number.isFinite(assetId)) return null;
  // Si viene de mesa: no achicar (máx. seguridad alto). Subida directa: tope suave.
  const fromPage =
    Number.isFinite(Number(raw.pageW)) &&
    Number.isFinite(Number(raw.pageH)) &&
    Number(raw.pageW) > 0 &&
    Number(raw.pageH) > 0;
  const { w, h } = fromPage
    ? {
        w: Math.max(0.02, finiteOr(raw.w, PASTEBOARD_ITEM_W)),
        h: Math.max(0.02, finiteOr(raw.h, PASTEBOARD_ITEM_H)),
      }
    : clampPasteboardSize(raw.w, raw.h, PASTEBOARD_ITEM_MAX);
  const pageW = Number(raw.pageW);
  const pageH = Number(raw.pageH);
  const pageX = Number(raw.pageX);
  const pageY = Number(raw.pageY);
  let zoom = Number(raw.zoom);
  if (!Number.isFinite(zoom)) zoom = 1;
  zoom = Math.min(8, Math.max(0.25, zoom));
  const panX = Number(raw.panX);
  const panY = Number(raw.panY);
  const rotation = Number(raw.rotation);
  const clip =
    raw.clip && typeof raw.clip === "object" ? { ...raw.clip } : null;
  return {
    id: String(raw.id || newPasteboardId()),
    assetId,
    x: finiteOr(raw.x, 0),
    y: finiteOr(raw.y, DEFAULT_ARTBOARD_H + ARTBOARD_GAP),
    w,
    h,
    // Round-trip: tamaño / crop en la mesa (norm 0–1)
    ...(Number.isFinite(pageW) && pageW > 0 ? { pageW } : null),
    ...(Number.isFinite(pageH) && pageH > 0 ? { pageH } : null),
    ...(Number.isFinite(pageX) ? { pageX } : null),
    ...(Number.isFinite(pageY) ? { pageY } : null),
    zoom: Math.round(zoom * 100) / 100,
    panX: Number.isFinite(panX) ? Math.min(1, Math.max(0, panX)) : 0.5,
    panY: Number.isFinite(panY) ? Math.min(1, Math.max(0, panY)) : 0.5,
    ...(Number.isFinite(rotation) && rotation
      ? { rotation: ((rotation % 360) + 360) % 360 }
      : null),
    ...(clip ? { clip } : null),
  };
}

/**
 * Tamaño en unidades de tablero = píxeles visuales de la casilla en la mesa
 * (slot frac × artboard). Sin achicar: fuera = mismo tamaño que dentro.
 */
export function pasteboardSizeFromSlot(slot, pageBoard) {
  const b = pageBoard || {
    w: DEFAULT_ARTBOARD_W,
    h: DEFAULT_ARTBOARD_H,
  };
  const sw = Math.max(0.02, Number(slot?.w) || PASTEBOARD_ITEM_W);
  const sh = Math.max(0.02, Number(slot?.h) || PASTEBOARD_ITEM_H);
  const bw = Math.max(0.01, Number(b.w) || DEFAULT_ARTBOARD_W);
  const bh = Math.max(0.01, Number(b.h) || DEFAULT_ARTBOARD_H);
  return {
    w: Math.max(0.02, sw * bw),
    h: Math.max(0.02, sh * bh),
  };
}

/**
 * Convierte un slot de página (coords 0–1 en artboard) a item de pasteboard
 * (coords de tablero), centrado donde estaba la foto en la mesa.
 * Conserva tamaño relativo, zoom/pan y rotación para el viaje de vuelta.
 */
export function slotToPasteboardItem(slot, pageBoard, { id } = {}) {
  const assetId = Number(slot?.assetId);
  if (!Number.isFinite(assetId)) return null;
  const b = pageBoard || {
    x: 0,
    y: 0,
    w: DEFAULT_ARTBOARD_W,
    h: DEFAULT_ARTBOARD_H,
  };
  const sx = Number(slot.x) || 0;
  const sy = Number(slot.y) || 0;
  const sw = Math.max(0.01, Number(slot.w) || 0.2);
  const sh = Math.max(0.01, Number(slot.h) || 0.2);
  const size = pasteboardSizeFromSlot(slot, b);
  const cx = Number(b.x) + (sx + sw / 2) * Number(b.w);
  const cy = Number(b.y) + (sy + sh / 2) * Number(b.h);
  return normalizePasteboardItem({
    id: id || newPasteboardId(),
    assetId,
    x: cx - size.w / 2,
    y: cy - size.h / 2,
    w: size.w,
    h: size.h,
    pageW: sw,
    pageH: sh,
    pageX: sx,
    pageY: sy,
    zoom: slot.zoom,
    panX: slot.panX,
    panY: slot.panY,
    rotation: slot.rotation,
    clip: slot.clip || null,
  });
}

/**
 * Slot de página a partir de un float del pasteboard + punto norm en artboard.
 * pageW/pageH = fracciones de mesa (ida). Si faltan: board units ÷ artboard
 * (nunca tratar item.h como fracción: bh≈0.7 acortaría en cada viaje).
 */
export function pasteboardItemToSlotDraft(
  item,
  placeAt = { x: 0.5, y: 0.5 },
  pageBoard = null,
) {
  const assetId = Number(item?.assetId);
  if (!Number.isFinite(assetId)) return null;
  const b = pageBoard || {
    w: DEFAULT_ARTBOARD_W,
    h: DEFAULT_ARTBOARD_H,
  };
  const bw = Math.max(0.01, Number(b.w) || DEFAULT_ARTBOARD_W);
  const bh = Math.max(0.01, Number(b.h) || DEFAULT_ARTBOARD_H);
  const pageW = Number(item?.pageW);
  const pageH = Number(item?.pageH);
  let slotW;
  let slotH;
  if (
    Number.isFinite(pageW) &&
    pageW > 0 &&
    Number.isFinite(pageH) &&
    pageH > 0
  ) {
    slotW = Math.min(1, Math.max(0.04, pageW));
    slotH = Math.min(1, Math.max(0.04, pageH));
  } else {
    // Board units → frac de página (inversa de pasteboardSizeFromSlot)
    slotW = Math.min(
      1,
      Math.max(0.04, (Number(item?.w) || PASTEBOARD_ITEM_W) / bw),
    );
    slotH = Math.min(
      1,
      Math.max(0.04, (Number(item?.h) || PASTEBOARD_ITEM_H) / bh),
    );
  }
  const cx = Math.min(0.95, Math.max(0.05, Number(placeAt?.x) || 0.5));
  const cy = Math.min(0.95, Math.max(0.05, Number(placeAt?.y) || 0.5));
  // Preferir caber entero: ajustar centro, no encoger w/h
  let x = cx - slotW / 2;
  let y = cy - slotH / 2;
  if (x < 0) x = 0;
  if (y < 0) y = 0;
  if (x + slotW > 1) x = Math.max(0, 1 - slotW);
  if (y + slotH > 1) y = Math.max(0, 1 - slotH);
  let zoom = Number(item?.zoom);
  if (!Number.isFinite(zoom)) zoom = 1;
  const panX = Number(item?.panX);
  const panY = Number(item?.panY);
  const rotation = Number(item?.rotation);
  // Clip en space:"local" no se traslada (va pegado al marco).
  // Legacy página: desplazar con el cambio de pageX/Y.
  const clip =
    item?.clip && typeof item.clip === "object" ? { ...item.clip } : null;
  let nextClip = clip;
  if (clip && String(clip.space || "").toLowerCase() !== "local") {
    if (
      Number.isFinite(Number(item.pageX)) &&
      Number.isFinite(Number(item.pageY))
    ) {
      const dx = x - Number(item.pageX);
      const dy = y - Number(item.pageY);
      if (Math.abs(dx) > 1e-9 || Math.abs(dy) > 1e-9) {
        if (clip.type === "path" && Array.isArray(clip.points)) {
          nextClip = {
            ...clip,
            points: clip.points.map((p) => ({
              x: (Number(p.x) || 0) + dx,
              y: (Number(p.y) || 0) + dy,
            })),
          };
        } else if (clip.x != null) {
          nextClip = {
            ...clip,
            x: (Number(clip.x) || 0) + dx,
            y: (Number(clip.y) || 0) + dy,
          };
        }
      }
    }
  }
  return {
    assetId,
    x,
    y,
    w: slotW,
    h: slotH,
    zoom: Math.min(8, Math.max(0.25, zoom)),
    panX: Number.isFinite(panX) ? panX : 0.5,
    panY: Number.isFinite(panY) ? panY : 0.5,
    ...(Number.isFinite(rotation) && rotation ? { rotation } : null),
    ...(nextClip ? { clip: nextClip } : null),
  };
}

/**
 * Mueve fotos (slots con assetId) de una mesa a otra sin pasar por el pasteboard.
 * Conserva tamaño, zoom/pan, rotación y clip; centra el grupo en placeAt (0–1).
 * @returns {{ pages: object[], movedIds: string[] }|null}
 */
export function reparentSlotsToPage(
  pages,
  {
    sourcePageIndex = 0,
    destPageIndex = 0,
    slotIds = [],
    placeAt = { x: 0.5, y: 0.5 },
  } = {},
) {
  const list = Array.isArray(pages) ? pages : [];
  const src = Math.floor(Number(sourcePageIndex));
  const dest = Math.floor(Number(destPageIndex));
  if (
    !Number.isFinite(src) ||
    !Number.isFinite(dest) ||
    src === dest ||
    src < 0 ||
    dest < 0 ||
    src >= list.length ||
    dest >= list.length
  ) {
    return null;
  }
  const idSet = new Set((slotIds || []).map(String));
  if (!idSet.size) return null;
  const sourcePage = list[src] || {};
  const destPage = list[dest] || {};
  const sourceSlots = Array.isArray(sourcePage.slots) ? sourcePage.slots : [];
  const destSlots = Array.isArray(destPage.slots) ? destPage.slots : [];
  const moving = sourceSlots.filter(
    (s) => idSet.has(String(s?.id)) && s?.assetId != null,
  );
  if (!moving.length) return null;

  const srcBoard = normalizePageBoard(sourcePage.board, src);
  const destBoard = normalizePageBoard(destPage.board, dest);
  const cx = Math.min(0.95, Math.max(0.05, Number(placeAt?.x) || 0.5));
  const cy = Math.min(0.95, Math.max(0.05, Number(placeAt?.y) || 0.5));
  const maxZ = destSlots.reduce((m, s) => Math.max(m, Number(s?.z) || 0), 0);
  const movedIds = [];
  const created = moving.map((s, i) => {
    const pb = slotToPasteboardItem(s, srcBoard);
    const draft = pasteboardItemToSlotDraft(
      pb,
      { x: cx + i * 0.04, y: cy + i * 0.04 },
      destBoard,
    );
    const id = String(s.id);
    movedIds.push(id);
    return {
      ...s,
      ...draft,
      id,
      assetId: Number(s.assetId),
      shape: s.shape || "rect",
      z: maxZ + 1 + i,
    };
  });
  const removeIds = new Set(movedIds);
  const next = list.map((p, i) => {
    if (i === src) {
      return {
        ...p,
        slots: sourceSlots.filter((s) => !removeIds.has(String(s?.id))),
      };
    }
    if (i === dest) {
      return {
        ...p,
        slots: [...destSlots, ...created],
      };
    }
    return p;
  });
  return { pages: next, movedIds };
}

export function normalizePasteboard(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  const seen = new Set();
  for (const item of raw) {
    const n = normalizePasteboardItem(item);
    if (!n || seen.has(n.id)) continue;
    seen.add(n.id);
    out.push(n);
    if (out.length >= 200) break;
  }
  return out;
}

/**
 * PagesJson storage:
 * - legacy: Page[]
 * - v2: { v: 2, pages: Page[], pasteboard: PasteboardItem[] }
 */
export function parseMoodboardDocument(raw) {
  let parsed = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { pages: null, pasteboard: [] };
    }
  }
  if (Array.isArray(parsed)) {
    return { pages: parsed, pasteboard: [] };
  }
  if (parsed && typeof parsed === "object" && Array.isArray(parsed.pages)) {
    return {
      pages: parsed.pages,
      pasteboard: normalizePasteboard(parsed.pasteboard),
    };
  }
  return { pages: null, pasteboard: [] };
}

export function serializeMoodboardDocument(pages, pasteboard = []) {
  const pageList = Array.isArray(pages) ? pages : [];
  const pb = normalizePasteboard(pasteboard);
  if (!pb.length) return pageList;
  return { v: 2, pages: pageList, pasteboard: pb };
}

/** Screen point → board units (needs camera + world bounds used for layout). */
export function clientToBoardPoint(clientX, clientY, viewportEl, cam, bounds) {
  if (!viewportEl || !cam || !bounds) return null;
  const rect = viewportEl.getBoundingClientRect();
  if (!(rect.width > 1) || !(rect.height > 1)) return null;
  const sx = clientX - rect.left;
  const sy = clientY - rect.top;
  const worldCssX = (sx - cam.panX) / Math.max(0.001, cam.zoom);
  const worldCssY = (sy - cam.panY) / Math.max(0.001, cam.zoom);
  return {
    x: bounds.minX + worldCssX / BOARD_PX_PER_UNIT,
    y: bounds.minY + worldCssY / BOARD_PX_PER_UNIT,
  };
}

/** Which artboard contains a board-unit point, or -1. */
export function artboardIndexAtPoint(pages, boardX, boardY) {
  const list = Array.isArray(pages) ? pages : [];
  for (let i = 0; i < list.length; i += 1) {
    const b = normalizePageBoard(list[i]?.board, i);
    if (
      boardX >= b.x &&
      boardX <= b.x + b.w &&
      boardY >= b.y &&
      boardY <= b.y + b.h
    ) {
      return i;
    }
  }
  return -1;
}

export function pointToArtboardNorm(pages, pageIndex, boardX, boardY) {
  const b = normalizePageBoard(
    (Array.isArray(pages) ? pages : [])[pageIndex]?.board,
    pageIndex,
  );
  return {
    x: Math.min(0.95, Math.max(0.05, (boardX - b.x) / Math.max(0.001, b.w))),
    y: Math.min(0.95, Math.max(0.05, (boardY - b.y) / Math.max(0.001, b.h))),
  };
}

export function defaultBoardForPageIndex(pageIndex = 0) {
  const i = Math.max(0, Math.floor(Number(pageIndex) || 0));
  return {
    x: i * (DEFAULT_ARTBOARD_W + ARTBOARD_GAP),
    y: 0,
    w: DEFAULT_ARTBOARD_W,
    h: DEFAULT_ARTBOARD_H,
  };
}

function finiteOr(n, fallback) {
  const v = Number(n);
  return Number.isFinite(v) ? v : fallback;
}

/** Clamp/normalize a board rect. If raw is missing, use default for pageIndex. */
export function normalizePageBoard(raw, pageIndex = 0) {
  const d = defaultBoardForPageIndex(pageIndex);
  if (!raw || typeof raw !== "object") return { ...d };
  const w = Math.min(8, Math.max(0.2, finiteOr(raw.w, d.w)));
  const h = Math.min(8, Math.max(0.2, finiteOr(raw.h, d.h)));
  return {
    x: finiteOr(raw.x, d.x),
    y: finiteOr(raw.y, d.y),
    w,
    h,
  };
}

/**
 * Ensure every page has a non-overlapping default board when missing.
 * Pages that already have board keep it (normalized).
 */
export function assignDefaultBoards(pages) {
  const list = Array.isArray(pages) ? pages : [];
  return list.map((p, i) => {
    const hasBoard =
      p?.board != null &&
      typeof p.board === "object" &&
      Number.isFinite(Number(p.board.x)) &&
      Number.isFinite(Number(p.board.y));
    return {
      ...p,
      board: hasBoard
        ? normalizePageBoard(p.board, i)
        : defaultBoardForPageIndex(i),
    };
  });
}

/**
 * Set artboard pose (board units). Content stays page-local.
 */
export function movePageBoard(pages, pageIndex, x, y) {
  const list = Array.isArray(pages) ? pages : [];
  if (!list.length) return list;
  const i = Math.max(
    0,
    Math.min(list.length - 1, Math.floor(Number(pageIndex) || 0)),
  );
  const nx = Number(x);
  const ny = Number(y);
  return list.map((p, idx) => {
    if (idx !== i) return p;
    const b = normalizePageBoard(p?.board, idx);
    return {
      ...p,
      board: {
        ...b,
        x: Number.isFinite(nx) ? nx : b.x,
        y: Number.isFinite(ny) ? ny : b.y,
      },
    };
  });
}

/**
 * Move a page in the array (Artboards panel / PDF order).
 * Keeps each page's board pose — spatial rearrange is separate.
 */
export function reorderPages(pages, fromIndex, toIndex) {
  const list = Array.isArray(pages) ? [...pages] : [];
  if (list.length < 2) return list;
  const from = Math.max(
    0,
    Math.min(list.length - 1, Math.floor(Number(fromIndex) || 0)),
  );
  const to = Math.max(
    0,
    Math.min(list.length - 1, Math.floor(Number(toIndex) || 0)),
  );
  if (from === to) return list;
  const [item] = list.splice(from, 1);
  list.splice(to, 0, item);
  return list;
}

/** Remap a selected index after reorderPages(from → to). */
export function remapIndexAfterReorder(index, fromIndex, toIndex) {
  const i = Math.floor(Number(index));
  const from = Math.floor(Number(fromIndex));
  const to = Math.floor(Number(toIndex));
  if (!Number.isFinite(i) || !Number.isFinite(from) || !Number.isFinite(to)) {
    return index;
  }
  if (from === to) return i;
  if (i === from) return to;
  if (from < to) {
    if (i > from && i <= to) return i - 1;
  } else if (i >= to && i < from) {
    return i + 1;
  }
  return i;
}

/**
 * Relayout artboard poses from current pages[] order (PDF order unchanged).
 * layout: row | column | grid. grid uses columns (default ceil sqrt n).
 */
export function rearrangeArtboards(
  pages,
  { layout = "row", columns = null, spacing = ARTBOARD_GAP } = {},
) {
  const list = Array.isArray(pages) ? pages : [];
  if (!list.length) return list;
  const gapRaw = Number(spacing);
  const gapN = Number.isFinite(gapRaw) && gapRaw >= 0 ? gapRaw : ARTBOARD_GAP;
  const mode = String(layout || "row").toLowerCase();
  const n = list.length;
  let cols;
  if (mode === "column") {
    cols = 1;
  } else if (mode === "grid") {
    const asked = Math.floor(Number(columns) || 0);
    cols = asked > 0 ? asked : Math.max(1, Math.ceil(Math.sqrt(n)));
  } else {
    cols = n;
  }

  return list.map((p, i) => {
    const prev = normalizePageBoard(p?.board, i);
    const col = i % cols;
    const row = Math.floor(i / cols);
    let x = 0;
    for (let c = 0; c < col; c += 1) {
      const idx = row * cols + c;
      const b = normalizePageBoard(list[idx]?.board, idx);
      x += b.w + gapN;
    }
    let y = 0;
    for (let r = 0; r < row; r += 1) {
      let maxH = 0;
      for (let c = 0; c < cols; c += 1) {
        const idx = r * cols + c;
        if (idx >= n) break;
        const b = normalizePageBoard(list[idx]?.board, idx);
        maxH = Math.max(maxH, b.h);
      }
      y += (maxH || DEFAULT_ARTBOARD_H) + gapN;
    }
    return {
      ...p,
      board: { x, y, w: prev.w, h: prev.h },
    };
  });
}

/** World AABB in board units (padding included). Includes pasteboard floats. */
/**
 * Bounds del mundo en board-units.
 * Origen (minX/minY) anclado a artboards — los floats del pasteboard solo
 * expanden maxX/maxY. Así al arrastrar una foto flotante las mesas no saltan
 * (antes minX/minY seguían al float y todo el layout se desplazaba).
 */
export function boardWorldBounds(pages, pad = 0.35, pasteboard = []) {
  const list = Array.isArray(pages) ? pages : [];
  const floats = normalizePasteboard(pasteboard);
  let artMinX = Infinity;
  let artMinY = Infinity;
  let artMaxX = -Infinity;
  let artMaxY = -Infinity;
  let anyArt = false;
  list.forEach((p, i) => {
    const b = normalizePageBoard(p?.board, i);
    artMinX = Math.min(artMinX, b.x);
    artMinY = Math.min(artMinY, b.y);
    artMaxX = Math.max(artMaxX, b.x + b.w);
    artMaxY = Math.max(artMaxY, b.y + b.h);
    anyArt = true;
  });
  if (!anyArt) {
    const d = defaultBoardForPageIndex(0);
    artMinX = 0;
    artMinY = 0;
    artMaxX = d.w;
    artMaxY = d.h;
  }
  // Origen fijo a mesas (+ pad). Floats fuera quedan con left/top negativos (overflow visible).
  const minX = artMinX - pad;
  const minY = artMinY - pad;
  let maxX = artMaxX + pad;
  let maxY = artMaxY + pad;
  floats.forEach((item) => {
    maxX = Math.max(maxX, item.x + item.w + pad);
    maxY = Math.max(maxY, item.y + item.h + pad);
    // Extender origen solo si el float queda a la izquierda/arriba — pero con
    // margen estable: no empujar por debajo del ancla de artboards.
    // (Si el float está a la izquierda, usamos CSS negativo; no movemos minX.)
  });
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: Math.max(0.01, maxX - minX),
    height: Math.max(0.01, maxY - minY),
  };
}

export function clampBoardCamZoom(z) {
  const n = Number(z);
  if (!Number.isFinite(n)) return 1;
  return (
    Math.round(
      Math.min(BOARD_CAM_ZOOM_MAX, Math.max(BOARD_CAM_ZOOM_MIN, n)) * 100,
    ) / 100
  );
}

/**
 * Camera that fits all artboards in a viewport (CSS px).
 * pan is the world-origin offset in CSS px before scale (transform: translate(pan) scale(z)).
 */
export function fitBoardCamera(
  pages,
  viewportW,
  viewportH,
  { pad = 0.35, marginPx = 48, pasteboard = [] } = {},
) {
  const bounds = boardWorldBounds(pages, pad, pasteboard);
  const worldW = Math.max(0.01, bounds.width) * BOARD_PX_PER_UNIT;
  const worldH = Math.max(0.01, bounds.height) * BOARD_PX_PER_UNIT;
  const vw = Math.max(1, Number(viewportW) || 1);
  const vh = Math.max(1, Number(viewportH) || 1);
  const m = Math.max(0, Number(marginPx) || 0);
  const zx = (vw - 2 * m) / worldW;
  const zy = (vh - 2 * m) / worldH;
  const zoom = clampBoardCamZoom(Math.min(zx, zy, 1));
  const contentW = worldW * zoom;
  const contentH = worldH * zoom;
  const panX = (vw - contentW) / 2 - bounds.minX * BOARD_PX_PER_UNIT * zoom;
  const panY = (vh - contentH) / 2 - bounds.minY * BOARD_PX_PER_UNIT * zoom;
  return { panX, panY, zoom, bounds };
}

/** Center camera on one artboard (page index). */
export function focusBoardCameraOnPage(
  pages,
  pageIndex,
  viewportW,
  viewportH,
  { targetZoom = 0.85 } = {},
) {
  const list = Array.isArray(pages) ? pages : [];
  const i = Math.max(
    0,
    Math.min(list.length - 1, Math.floor(Number(pageIndex) || 0)),
  );
  const b = normalizePageBoard(list[i]?.board, i);
  const zoom = clampBoardCamZoom(targetZoom);
  const vw = Math.max(1, Number(viewportW) || 1);
  const vh = Math.max(1, Number(viewportH) || 1);
  const artW = b.w * BOARD_PX_PER_UNIT * zoom;
  const artH = b.h * BOARD_PX_PER_UNIT * zoom;
  const panX = vw / 2 - (b.x + b.w / 2) * BOARD_PX_PER_UNIT * zoom;
  const panY = vh / 2 - (b.y + b.h / 2) * BOARD_PX_PER_UNIT * zoom;
  // Nudge if artboard larger than viewport — top-left bias
  const adjX = artW > vw ? vw * 0.08 - b.x * BOARD_PX_PER_UNIT * zoom : panX;
  const adjY = artH > vh ? vh * 0.08 - b.y * BOARD_PX_PER_UNIT * zoom : panY;
  return {
    panX: artW > vw ? adjX : panX,
    panY: artH > vh ? adjY : panY,
    zoom,
  };
}

export function readStoredWorkspaceMode() {
  if (typeof window === "undefined") return WORKSPACE_MODE_BOARD;
  try {
    const v = window.localStorage.getItem(WORKSPACE_MODE_STORAGE_KEY);
    if (v === WORKSPACE_MODE_PAGE || v === WORKSPACE_MODE_BOARD) return v;
  } catch {
    /* ignore */
  }
  return WORKSPACE_MODE_BOARD;
}

export function writeStoredWorkspaceMode(mode) {
  if (typeof window === "undefined") return;
  const v =
    mode === WORKSPACE_MODE_PAGE ? WORKSPACE_MODE_PAGE : WORKSPACE_MODE_BOARD;
  try {
    window.localStorage.setItem(WORKSPACE_MODE_STORAGE_KEY, v);
  } catch {
    /* ignore */
  }
}
